"use client";

import { useSyncExternalStore } from "react";

/**
 * Tiny cross-component flag: "is an APP FRAME on screen?" — a page pinned to
 * the viewport (`fixed inset-x-0 top-0 h-dvh`) whose inner panel scrolls while
 * the document does not. Today that is `/chat`, plus the auth pages (login,
 * signup, reset, verify, confirm), which have no Nav at all and render the
 * strips in flow for the same reason (2026-09-26, `(auth)/layout.tsx`).
 *
 * ⛔ WHY THE STRIPS NEED TO KNOW. `CookieConsent` and `LocaleSuggestion` hang
 * off `--sticky-top`, the Nav height `NavScrollWrapper` publishes. An app frame
 * renders its OWN Nav inside itself and never publishes that variable, so
 * there it holds whatever the PREVIOUS page left on `document.documentElement`
 * — measured 2026-09-05: 114px inherited from the homepage, against a chat Nav
 * of 78px — and the `0px` fallback covers the frame's own header. So an app
 * frame mounts its own copies in flow (`variant="inline"`) and the fixed ones
 * in the root layout stand down.
 *
 * ⭐ A STORE, NOT A ROUTE TEST. The first version read `window.location` in a
 * mount effect, which is wrong in a way lint noticed for a different reason:
 * the root layout is NOT remounted on client-side navigation, so a visitor
 * going `/` → `/chat` in-app would keep the stale answer and the fixed banner
 * would reappear over the frame. Mount/unmount is the signal, so let the frame
 * declare itself. Refcounted like `body-scroll-lock`, because both inline
 * strips register and either may unmount first.
 *
 * `getServerSnapshot` returns false: no app frame exists until something
 * mounts and says so, which is also what SSR renders.
 */
let count = 0;
const listeners = new Set<() => void>();

function emit() {
  listeners.forEach((l) => l());
}

export function acquireAppFrame(): void {
  count += 1;
  if (count === 1) emit();
}

export function releaseAppFrame(): void {
  if (count === 0) return; // defensive — no-op if already cleared
  count -= 1;
  if (count === 0) emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const getSnapshot = () => count > 0;
const getServerSnapshot = () => false;

export function useAppFrameMounted(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * The same answer read LIVE, for use inside an effect.
 *
 * ⛔ An effect must not gate on the hook's value: effects run children-first,
 * so the inline copy has already acquired by the time a parent's effect runs,
 * but that parent's closure still holds the value from the render that
 * scheduled it — `false`. Gating on it let the FIXED strip claim an app-frame
 * route (review finding, 2026-09-05). Read the store instead of the snapshot.
 */
export function isAppFrameMounted(): boolean {
  return count > 0;
}
